import { Button, theme } from 'aimo-ui';
import InfoBox from 'components/common/InfoBox';
import { InfoIcon } from 'components/icons/InfoIcon';
import PaymentCardDropDown from 'components/park/detail/PaymentCardDropDown';
import { FooterButtonContainer } from 'components/sheets/SheetLayout';
import useAddPaymentMethod from 'hooks/useAddPaymentMethod';
import useRetryRecurringPayment from 'hooks/useRetryRecurringPayment';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import useSubscriptions from 'hooks/useSubscriptions';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parkingPaymentCardId } from 'states/common';
import { useSubscriptionStore } from 'stores/subscriptionStore';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';

const SubscriptionFooter: React.FC = () => {
  const { t } = useTranslation();
  const selectedPaymentMethod = useSelectedPaymentMethod();
  const { isRecurring } = useSubscriptionStore();
  const { addCard, loadingAddCard } = useAddPaymentMethod();
  const {
    subscription,
    purchaseSubscription,
    updateSalesOrder,
    updateLoading,
    loadingSalesOrder,
  } = useSubscriptions();

  const { orderLines } = subscription ?? {};

  useEffect(() => {
    if (subscription?.creditCardId) {
      parkingPaymentCardId(subscription.creditCardId);
    }
  }, [subscription?.creditCardId]);

  const { salesOrderId, quantity, version, pmcId, orderLineId } =
    orderLines?.[0] ?? {};

  const hasRenewalChanged = subscription?.renewal !== isRecurring;
  const hasPaymentMethodChanged =
    selectedPaymentMethod?.cardId !== subscription?.creditCardId;

  const { retryPayment, isProcessing, isFailed } = useRetryRecurringPayment({
    salesOrderUid: subscription?.uid,
  });
  // Aimo+ is card-only (a CA carried over from parking can't pay for it):
  // like parking, no card selected means the button adds one
  const needsCard = !selectedPaymentMethod?.cardId;

  const onPress = async () => {
    if (needsCard) {
      return addCard();
    }
    if (isFailed) {
      return retryPayment(selectedPaymentMethod?.cardId ?? undefined);
    }
    if (!subscription?.uid) {
      return purchaseSubscription();
    }
    return await updateSalesOrder({
      variables: {
        input: {
          salesOrderId: salesOrderId ?? '',
          pmcId: pmcId ?? '',
          quantity: quantity ?? 1,
          version: version ?? 1,
          creditCardId: selectedPaymentMethod?.cardId ?? '',
          renewal: isRecurring,
          orderLineId: orderLineId ?? '',
          registrationPlates: [],
          updateType: 'PATCH',
        },
      },
    });
  };

  const getButtonText = () => {
    if (isProcessing) return t('button.processingPayment');
    if (needsCard) return t('onboarding.addPaymentMethod');
    if (isFailed) return t('button.retryPayment');
    if (subscription?.uid) return t('button.saveChanges');
    return t('button.continue');
  };

  const hasUnsavedChanges = useMemo(
    () => hasRenewalChanged || hasPaymentMethodChanged,
    [hasRenewalChanged, hasPaymentMethodChanged]
  );
  const buttonDisabled =
    !needsCard && (subscription?.uid ? !hasUnsavedChanges : !subscription);
  return (
    <ColumnWithMargins>
      <StyledWrapper mb={0}>
        <FooterButtonContainer pb={0} pt={0}>
          {isFailed && (
            <InfoBox
              bgColor={theme.colors.lightBg.yellow}
              padding={10}
              radius={20}
              leftComponent={<InfoIcon />}
              margin={0}
              text={`${t('permit.paymentFailed')}. ${t('permit.resolvePaymentIntro')}`}
            />
          )}
        </FooterButtonContainer>
        <FooterButtonContainer>
          <StyledWrapper alignItems="flex-end" flex={1} mb={50}>
            <PaymentCardDropDown recurringOnly />
          </StyledWrapper>
        </FooterButtonContainer>
        <FooterButtonContainer>
          <Button
            loading={loadingSalesOrder || updateLoading || loadingAddCard}
            disabled={buttonDisabled || updateLoading}
            onPress={onPress}
            size="l"
            text={getButtonText()}
            testID="subscription-continue"
          />
        </FooterButtonContainer>
      </StyledWrapper>
    </ColumnWithMargins>
  );
};

export default SubscriptionFooter;
